import { get, find, isNil, some } from 'lodash';
import { Account } from '@core/api/types';

interface PackageMap {
  fraud_prevention: boolean;
  audience_builder: boolean;
  uninstall_reinstall: boolean;
  purchase_verification: boolean;
  multitouch: boolean;
  ad_revenue: boolean;
  web_attribution: boolean;
}

export const isTodayAfterActivationDate = (activationUTCDateString: string) =>
  Date.now() > Date.parse(activationUTCDateString);

export const hasNewFeatureScope = (account: Nullable<Account>) => Boolean(account?.new_feature_scope);
export const hasNoPaymentDetails = (account: Nullable<Account>) => Boolean(account?.missing_payment_information);

export const hasFeature = (account: Nullable<Account>, feature: string): boolean =>
  get(account, `features.${feature}.enabled`, false);

export const isPackageActive = (account: Nullable<Account>, packageName: string): boolean => {
  if (!account) {
    return false;
  }

  const found = find(account.packages, { name: packageName });

  if (isNil(found) || !found.id) {
    return false;
  }

  return isTodayAfterActivationDate(found.active_since);
};

export const hasPackage = (account: Nullable<Account>, feature: string, packageName?: string): boolean => {
  if (!account) {
    return false;
  }

  if (hasNewFeatureScope(account)) {
    return hasFeature(account, feature);
  }

  const isFeatureEnabled = !!account.features?.[feature]?.enabled;

  return isFeatureEnabled || isPackageActive(account, packageName || feature);
};

export const hasFraudPackage = (account: Nullable<Account>): boolean => hasPackage(account, 'fraud_prevention');
export const hasUninstallPackage = (account: Nullable<Account>): boolean => hasPackage(account, 'uninstall_reinstall');
export const hasAdRevenuePackage = (account: Nullable<Account>): boolean => hasPackage(account, 'ad_revenue');
export const hasControlCenterPackage = (account: Nullable<Account>): boolean => hasPackage(account, 'control_center');
export const hasRevenueDataFeature = (account: Nullable<Account>): boolean => hasFeature(account, 'revenue_data');
export const hasAdvancedPermissionsPackage = (account: Nullable<Account>): boolean => hasPackage(account, 'advanced_permissions');
export const hasMultitouchPackage = (account: Nullable<Account>): boolean => hasPackage(account, 'multitouch');
export const hasSubscriptionPackage = (account: Nullable<Account>): boolean => hasPackage(account, 'subscription');
export const hasWebAttributionPackage = (account: Nullable<Account>): boolean => hasPackage(account, 'web_attribution');

export const hasSegmentationPackage = (account: Nullable<Account>): boolean =>
  hasPackage(account, 'segmentation', 'audience_builder');

export const hasSecurityPackage = (account: Nullable<Account>): boolean =>
  hasPackage(account, 'security') || hasPackage(account, 'sso');

export const hasPurchaseVerificationPackage = (account: Nullable<Account>, platforms = undefined): boolean => {
  if (platforms && !hasAndroidOrIOSConfigured(platforms)) {
    return false;
  }

  return hasPackage(account, 'purchase_verification');
};

export const hasAndroidOrIOSConfigured = (platforms): boolean =>
  some([get(platforms, 'android.configured', false), get(platforms, 'ios.configured', false)]);

export const isInSetupPhase = (stateName: string): boolean => ['setup_app', 'waiting_for_data'].includes(stateName);

export const getPackagesMap = (account: Nullable<Account>): PackageMap => ({
  fraud_prevention: hasFraudPackage(account),
  audience_builder: hasSegmentationPackage(account),
  uninstall_reinstall: hasUninstallPackage(account),
  purchase_verification: hasPurchaseVerificationPackage(account),
  multitouch: hasMultitouchPackage(account),
  ad_revenue: hasAdRevenuePackage(account),
  web_attribution: hasWebAttributionPackage(account),
});
